import { applySeo } from "@/lib/seo-head";
import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import siteConfig from "@/config/site.json";
import { useT } from "@/lib/i18n";

export const Route = createFileRoute("/cookies")({
  component: CookiesPage,
  head: (ctx) => applySeo(ctx.matches, ((_c: unknown) => ({
    meta: [
      { title: `Política de Cookies — ${siteConfig.brand.name}` },
      {
        name: "description",
        content:
          "Información sobre las cookies utilizadas en el sitio web de RR Lex Juristas: tipos, finalidad y cómo gestionarlas.",
      },
      { property: "og:title", content: `Política de Cookies — ${siteConfig.brand.name}` },
      {
        property: "og:description",
        content:
          "Información sobre las cookies utilizadas en el sitio web de RR Lex Juristas: tipos, finalidad y cómo gestionarlas.",
      },
      { property: "og:url", content: "https://rrlexjuristas.com/cookies" },
    ],
    links: [
      { rel: "canonical", href: "https://rrlexjuristas.com/cookies" },
    ],
  }))(ctx)),
});

function CookiesPage() {
  return (
    <Layout>
      <article className="max-w-3xl mx-auto px-6 py-20">
        <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4">Legal</p>
        <h1 className="text-4xl md:text-5xl font-serif mb-8">Política de Cookies</h1>
        <div className="gold-divider mb-10" />

        <div className="space-y-6 text-foreground/80 leading-relaxed">
          <p>
            Esta página explica qué son las cookies, qué tipos utilizamos en{" "}
            {siteConfig.brand.name} y cómo puede gestionarlas en su navegador.
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">1. ¿Qué son las cookies?</h2>
          <p>
            Las cookies son pequeños archivos de texto que los sitios web
            almacenan en su dispositivo para recordar información sobre su
            visita, como sus preferencias de idioma u otras opciones.
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">2. Tipos de cookies que utilizamos</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong className="text-foreground">Cookies técnicas:</strong>{" "}
              necesarias para el correcto funcionamiento del sitio.
            </li>
            <li>
              <strong className="text-foreground">Cookies de preferencias:</strong>{" "}
              recuerdan opciones como el idioma seleccionado.
            </li>
            <li>
              <strong className="text-foreground">Cookies analíticas:</strong>{" "}
              nos permiten medir el uso del sitio de forma agregada y anónima
              para mejorar la experiencia.
            </li>
          </ul>

          <h2 className="font-serif text-2xl text-foreground mt-10">3. Gestión de cookies</h2>
          <p>
            Puede aceptar, rechazar o eliminar las cookies en cualquier momento
            desde la configuración de su navegador. Tenga en cuenta que la
            desactivación de algunas cookies puede afectar al funcionamiento del
            sitio.
          </p>
          <div className="pt-4">
            <CookiePreferencesButton />
          </div>

          <h2 className="font-serif text-2xl text-foreground mt-10">4. Más información</h2>
          <p>
            Para cualquier duda sobre esta política, puede contactarnos en{" "}
            <a className="text-gold hover:underline" href={`mailto:${siteConfig.contact.emails[0].address}`}>
              {siteConfig.contact.emails[0].address}
            </a>
            .
          </p>
        </div>
      </article>
    </Layout>
  );
}

function CookiePreferencesButton() {
  const { tk } = useT();
  return (
    <button
      type="button"
      onClick={() => window.pulso33Consent?.open()}
      className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-sm tracking-[0.2em] uppercase text-gold hover:bg-gold hover:text-primary-foreground transition-colors font-semibold"
    >
      {tk("cookies.preferencesButton", "Gestionar preferencias de cookies")}
    </button>
  );
}
